import { Link } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { SOCIAL_LINKS } from '@/lib/constants';
import { theme } from '@/lib/ui/theme';

interface FooterProps {
  isLoading?: boolean;
  // minimal drops the marketing links (Get started/Pricing/FAQ/Docs/Philosophy),
  // leaving only Contact + Terms + Privacy. Used on focused pages like onboarding
  // where extra exits hurt conversion.
  minimal?: boolean;
}

export function Footer({ isLoading = false, minimal = false }: FooterProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  const linkClass = 'text-foreground/70 transition-all hover:underline';
  const linkStyle = {
    fontFamily: theme.fonts.body,
    textDecorationColor: theme.colors.semantic.info,
  };
  // Internal router links. Marketing links drop in `minimal` mode; legal links
  // always show. External links (Blog, Status) are plain anchors below.
  const marketingLinks = [
    { to: '/getting-started', label: 'Get started' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/faq', label: 'FAQ' },
    { to: '/docs', label: 'Docs' },
    { to: '/philosophy', label: 'Philosophy' },
    { to: '/consulting', label: 'Consulting' },
  ];
  const legalLinks = [
    { to: '/contact', label: 'Contact' },
    { to: '/terms', label: 'Terms' },
    { to: '/privacy', label: 'Privacy' },
  ];

  if (isLoading) {
    return (
      <footer
        className="px-6 py-12 md:px-16"
        style={{
          backgroundColor: 'var(--section-background)',
        }}
      >
        <div
          className="mx-auto flex flex-col items-center gap-6"
          style={{ maxWidth: theme.spacing.maxWidth }}
        >
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-32" />
          <div className="flex gap-6">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-16" />
          </div>
          <Skeleton className="h-4 w-48" />
        </div>
      </footer>
    );
  }

  return (
    <footer
      className="px-6 py-12 md:px-16"
      style={{
        backgroundColor: 'var(--section-background)',
      }}
    >
      <div
        className="mx-auto flex flex-col items-center gap-6"
        style={{ maxWidth: theme.spacing.maxWidth }}
      >
        {/* Logo + Tagline on same line */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-3 transition-opacity hover:opacity-80"
          >
            <div
              className="flex h-10 w-10 items-center justify-center rounded-lg text-2xl"
              style={{
                background: '#5b21b6',
                fontFamily: theme.fonts.display,
                textShadow: '2px 3px 4px rgba(0, 0, 0, 0.5)',
              }}
            >
              🪲
            </div>
            <span
              className="text-gradient-hero text-xl font-bold"
              style={{
                fontFamily: theme.fonts.display,
              }}
            >
              Mindrealm
            </span>
          </Link>
          <span className="text-foreground/50">·</span>
          <p
            className="text-lg font-semibold md:text-xl"
            style={{
              fontFamily: theme.fonts.display,
              color: isDark ? '#ffffff' : '#1f2937',
            }}
          >
            Judgment enhances generation.
          </p>
        </div>

        {/* Links */}
        <div className="flex flex-wrap justify-center gap-6">
          {!minimal && (
            <>
              {marketingLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={linkClass}
                  style={linkStyle}
                >
                  {link.label}
                </Link>
              ))}
              {/* External (Hashnode-hosted blog) — a plain anchor, not a router Link. */}
              <a
                href={SOCIAL_LINKS.blog}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
                style={linkStyle}
              >
                Blog
              </a>
            </>
          )}
          {legalLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={linkClass}
              style={linkStyle}
            >
              {link.label}
            </Link>
          ))}
          {/* X profile. rel="me" ties the account to the site for identity verification. */}
          <a
            href={SOCIAL_LINKS.x}
            target="_blank"
            rel="me noopener noreferrer"
            className={linkClass}
            style={linkStyle}
          >
            X
          </a>
          {/* External (public uptime page) — a plain anchor, not a router Link. */}
          <a
            href={SOCIAL_LINKS.status}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
            style={linkStyle}
          >
            Status
          </a>
        </div>

        {/* Copyright */}
        <p
          className="text-foreground/50 text-sm"
          style={{ fontFamily: theme.fonts.body }}
        >
          © {new Date().getFullYear()} Mindrealm
        </p>
      </div>
    </footer>
  );
}
